import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { PhoneNumberInput } from '@change/ds-forms';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { createAnchorToLinkReplacer } from '#src/shared/ds';

import type { UseJournalistOptInFormReturn } from '#src/app/shared/hooks/phoneOptIn';
import { isPhoneNumberCountry } from '#src/app/shared/utils/phoneCollection';

import { JournalistOptInSubhead } from '../JournalistOptInSubhead';

/**
 * The media-outreach consent ask. Drive it with `useJournalistOptInForm`.
 *
 * With a number already on the account the field is read-only: this screen asks for a consent
 * decision, not for a number.
 */
export const JournalistOptInScreen: React.FC<JournalistOptInScreenProps> = ({
	data,
	onChange,
	onLearnMoreClick,
	inputRef,
	showHeading = true,
	placeholder,
	children,
}) => {
	const { translate } = useI18n();

	const defaultCountry = isPhoneNumberCountry(data.countryCode) ? data.countryCode : undefined;
	const label = translate('corgi.petition.starter_onboarding.phone_collection_step_journalist.input_label');
	return (
		<div className="flex flex-col gap-8">
			{showHeading && (
				<div className="flex flex-col gap-8">
					<Text variant="heading" size="large" as="h1">
						<Translate value="corgi.petition.starter_onboarding.phone_collection_step_journalist.title" />
					</Text>
					<Text as="div" size="large">
						<JournalistOptInSubhead onLearnMoreClick={onLearnMoreClick} />
					</Text>
				</div>
			)}
			<div className="flex flex-col gap-4">
				<div>
					<div className="mb-1 flex items-center gap-1">
						<Text variant="heading" size="xx-small">
							<Translate value="corgi.petition.starter_onboarding.phone_collection_step_journalist.input_label" />
						</Text>
						{!data.hasPhone && (
							<Text size="small" className="text-typography-weak">
								<Translate value="corgi.petition.starter_onboarding.phone_collection_step_journalist.optional_label" />
							</Text>
						)}
					</div>
					{data.hasPhone ? (
						<PhoneNumberInput
							name="starter-onboarding-phone-collection-journalist-input"
							label={label}
							labelIsVisible={false}
							data-testid="starter-onboarding-phone-collection-journalist-input"
							data-qa="starter-onboarding-phone-collection-journalist-input"
							value={data.phoneNumber || undefined}
							isDisabled
							size="large"
						/>
					) : (
						<PhoneNumberInput
							ref={inputRef}
							name="starter-onboarding-phone-collection-journalist-input"
							label={label}
							labelIsVisible={false}
							data-testid="starter-onboarding-phone-collection-journalist-input"
							data-qa="starter-onboarding-phone-collection-journalist-input"
							defaultCountry={defaultCountry}
							placeholder={placeholder}
							value={data.inputValue || undefined}
							onChange={onChange}
							errorMessage={data.inputError}
							isInvalid={Boolean(data.inputError)}
							size="large"
						/>
					)}
				</div>

				<Text as="div" size="x-small" className="text-typography-weak">
					<Translate value="corgi.petition.starter_onboarding.phone_collection_step_journalist.consent_disclosure" />{' '}
					<TranslateHtml
						value="corgi.petition.starter_onboarding.phone_collection_step_journalist.terms"
						replacements={{ tosUrl: '/policies/terms-of-service', privacyUrl: '/policies/privacy' }}
						replace={createAnchorToLinkReplacer({ size: 'x-small', className: 'text-typography-weak' })}
					/>
				</Text>

				{children}
			</div>
		</div>
	);
};

type JournalistOptInScreenProps = Readonly<{
	data: UseJournalistOptInFormReturn['data'];
	onChange: (value: string | undefined) => void;
	onLearnMoreClick: () => void;
	inputRef: React.Ref<HTMLInputElement>;
	/**
	 * Whether the screen renders its own headline and subhead. Set false when the host already shows
	 * them, so they are not rendered twice.
	 */
	showHeading?: boolean;
	/** Never applied to the read-only field shown for a number already on the account. */
	placeholder?: string;
	/** The actions row, rendered below the disclosure. Omit it to render actions outside the screen. */
	children?: React.ReactNode;
}>;
